/**
 * BilledFeeSync — manual "Run now" trigger for api/sync-billed-fee.js, the nightly Vercel Cron
 * that re-pulls BQE Core's invoice history and corrects each Phase's Airtable "Billed Fee" (and
 * therefore its derived Effective Billing Rate — see docs/BILLING_BQE.md). Exists so a run can be
 * tested/verified on demand instead of waiting for the 9am UTC schedule.
 */
import { useEffect, useState } from 'react'
import { confirmDialog } from '../Dialog.jsx'
import { syncBilledFee, getBilledFeeSyncStatus } from '../../services/timecards.js'

// "3m ago" / "2h ago" / "5d ago" / a plain date past a week — same convention as the matching
// "Run now" button in TimeReviewModal.jsx.
function fmtAgo(iso) {
  if (!iso) return 'never'
  let ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) ms = 0
  const mins = Math.floor(ms / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return mins + 'm ago'
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return hrs + 'h ago'
  const days = Math.floor(hrs / 24)
  if (days < 7) return days + 'd ago'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function BilledFeeSync({ dark }) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [err, setErr] = useState(null)
  const [lastRunAt, setLastRunAt] = useState(null)

  const textPrim = dark ? 'rgba(207,226,232,0.92)' : '#07272D'
  const textMut  = dark ? 'rgba(91,132,148,0.80)'  : 'rgba(7,39,45,0.55)'

  useEffect(() => { getBilledFeeSyncStatus().then(setLastRunAt).catch(() => {}) }, [])

  async function run() {
    if (!(await confirmDialog({
      title: 'Sync Billed Fee now?',
      message: 'Re-pulls BQE Core’s whole invoice history and corrects any Phase whose Airtable Billed Fee has drifted from what Core actually shows. Same job that runs nightly — safe to run any time.',
      confirmLabel: 'Run sync',
    }))) return
    setBusy(true); setErr(null); setResult(null)
    try {
      const d = await syncBilledFee()
      setResult(d)
      if (d.lastRunAt) setLastRunAt(d.lastRunAt)
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const btn = {
    padding: '8px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
    borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    background: '#1f9d6b', color: '#fff', border: 'none',
  }

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: textMut, marginBottom: 14, maxWidth: 680 }}>
        Runs nightly at 9am UTC on its own (Vercel Cron). This button fires the same job on demand
        for testing — it aborts and writes nothing if the Core invoice fetch doesn't complete
        cleanly, so it's safe to run any time.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button style={btn} disabled={busy} onClick={run}>{busy ? 'Syncing…' : 'Run now'}</button>
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: textMut }} title={lastRunAt ? new Date(lastRunAt).toLocaleString('en-US') : 'This sync has never run'}>
          Last synced {fmtAgo(lastRunAt)}
        </span>
      </div>
      {result && (
        <div style={{ marginTop: 10, fontFamily: '"DM Mono",monospace', fontSize: 12, color: textPrim }}>
          ✓ Done. scanned {result.scannedPhases} phase(s) · {result.invoiceCount} Core invoice(s) · updated {result.updated}
        </div>
      )}
      {err && <div style={{ marginTop: 10, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b', maxWidth: 680 }}>{err}</div>}
    </div>
  )
}
